import { I18n } from "@assets/strings";
import React from "react";
import { useHistory } from "@core/routing/useHistory";
import styled, { css } from "styled-components";
import { Path } from "@core/routing/path";
import { useServices } from "@core/servicesContext";
import { useIsPerson } from "@domain/currentProfile/hooks";
import { categoryIcons } from "@domain/lms/category/category";
import { Quiz } from "@domain/lms/quiz/quiz";
import { getDisplayedAuthor } from "@domain/lms/utils";
import { useHover, useIsMobile } from "@utils/hooks";
import { useModalTools } from "@ui/modals/modalContext";
import { useConfirmation } from "@ui/modals/useConfirmation";
import { theme } from "@ui/styles/theme";
import { createToast } from "@ui/toasts/utils";
import { BadgeLabel } from "@ui/components/badgeLabel";
import { PrimaryButton } from "@ui/components/buttons/primaryButton";
import { CategoryLabel } from "@ui/components/categoryLabel";
import { Clamp } from "@ui/components/containers/clamp";
import { DraftRenderer } from "@ui/components/containers/draftRenderer";
import { Primary } from "@ui/components/containers/primary";
import { CourseStatusLabel } from "@ui/components/course/courseStatusLabel";
import { CommunityIcon, LevelOneIcon, LevelThreeIcon, LevelTwoIcon, QuizIcon, RepostIcon } from "@ui/components/icons";
import { Row, row, Stack, stack } from "@ui/components/layout";
import { AuthLink } from "@ui/components/linkExternal";
import { KebabMenu } from "@ui/components/menus/kebabMenu";
import { MenuRow } from "@ui/components/menus/menuRow";
import { IconWrapper, SimpleMenuContent } from "@ui/components/menus/simpleMenuContent";
import { EditReferencePostModal } from "@ui/components/post/reference/editReferencePostModal";
import { UserView } from "@ui/components/profile/userView";
import { SubAuthor } from "@ui/components/subAuthor";
import { QuizParticipantModal } from "./quizParticipantModal";

interface QuizThumbnailProps extends React.HTMLAttributes<HTMLDivElement> {
	quiz: Quiz;
	displayStatus?: boolean;
	small?: boolean;
}

export const QuizThumbnail: React.FC<QuizThumbnailProps> = ({
	quiz,
	displayStatus,
	small: forceSmall,
	...htmlProps
}) => {
	const { id, category, title, description, participants, exercises, status, author, color, level } = quiz;
	const { quizService } = useServices();
	const isMobile = useIsMobile();
	const history = useHistory();
	const displayedAuthor = getDisplayedAuthor(quiz);
	const isAuthor = useIsPerson({ id: displayedAuthor?.id });
	const { createModal } = useModalTools();
	const subAuthor = displayedAuthor !== author ? author : undefined;
	const smallVersion = forceSmall || isMobile;
	const { ref, isHovered } = useHover();

	const askDeleteConfirmation = useConfirmation(
		I18n.t("quiz.delete.title"),
		I18n.t("delete"),
		I18n.t("quiz.delete.message")
	);

	return (
		<Container {...htmlProps} small={smallVersion} color={color} ref={ref}>
			{category && category !== "OTHERS" && (
				<StyledCategoryLabel
					category={category}
					small={smallVersion}
					isSmall={smallVersion}
					renderIcons={() => <QuizIcon color={theme.colors.white} />}
					isTitleTruncated={true}
				/>
			)}
			{!smallVersion && (
				<StyledBadgeLabel
					isHovered={isHovered}
					hoverWidth={118}
					badgeTitle={I18n.t("quiz.thumbnail.badge_label")}
					renderIcons={() => <QuizIcon color={theme.colors.secondary} />}
				/>
			)}
			{!smallVersion && <CategoryImage src={categoryIcons[category ?? "OTHERS"]} />}
			<Clamp size={smallVersion ? 1 : 2}>
				<Title small={smallVersion}>{title}</Title>
			</Clamp>
			<Clamp size={smallVersion ? 2 : 3}>
				<Description content={description} small={smallVersion} />
			</Clamp>
			<Row gap={15} wrap style={{ display: "flex", alignItems: "self-end" }}>
				{!smallVersion ? (
					<>
						<Stack gap={4} align="center">
							<WhitePrimary style={{ fontSize: "24px" }}>{exercises.size}</WhitePrimary>
							<InfoIconWrapper>
								<QuizIcon /> {I18n.t("quiz.infos.questions", { count: exercises.size })}
							</InfoIconWrapper>
						</Stack>
						<Stack
							gap={4}
							align="center"
							style={{ cursor: "pointer" }}
							onClick={() => {
								createModal((close) => <QuizParticipantModal quiz={quiz} close={close} />);
							}}
						>
							<WhitePrimary style={{ fontSize: "24px" }}>{participants.count}</WhitePrimary>
							<InfoIconWrapper>
								<CommunityIcon /> {I18n.t("quiz.thumbnail.participants", { count: participants.count })}
							</InfoIconWrapper>
						</Stack>
					</>
				) : (
					<>
						<Row gap={4} align="center">
							<InfoIconWrapper>
								<QuizIcon />
							</InfoIconWrapper>
							<WhitePrimary>
								{smallVersion ? exercises.size : I18n.t("quiz.infos.questions", { count: exercises.size })}
							</WhitePrimary>
						</Row>
						<Row gap={4} align="center">
							<InfoIconWrapper>
								<CommunityIcon />
							</InfoIconWrapper>
							<WhitePrimary
								onClick={() => {
									createModal((close) => <QuizParticipantModal quiz={quiz} close={close} />);
								}}
							>
								{smallVersion
									? participants.count
									: I18n.t("quiz.thumbnail.participants", { count: participants.count })}
							</WhitePrimary>
						</Row>
					</>
				)}

				{level && (
					<Row gap={4} align="center">
						{level == "BEGINNER" ? <LevelOneIcon color={theme.colors.white} /> : undefined}
						{level == "INTERMEDIATE" ? <LevelTwoIcon color={theme.colors.white} /> : undefined}
						{level == "ADVANCED" ? <LevelThreeIcon color={theme.colors.white} /> : undefined}
						<WhitePrimary>{I18n.t(`quiz.level.${level}`)} </WhitePrimary>
					</Row>
				)}
			</Row>
			<StyledUserView
				user={displayedAuthor}
				alignStart={!!subAuthor}
				hasBorder={true}
				renderBottomAccessory={subAuthor ? () => <SubAuthor author={subAuthor} displayAuthorAsMe /> : undefined}
			/>
			<StyledMenu blur authOnly small={smallVersion}>
				<SimpleMenuContent
					onEdit={isAuthor ? () => history.push(Path.QuizEditor(id)) : undefined}
					onDelete={
						isAuthor
							? () =>
									askDeleteConfirmation(async () => {
										await quizService.deleteQuiz(id);
										createToast(I18n.t("quiz.toast.delete.title"), I18n.t("quiz.toast.delete.message"), "success");
									})
							: undefined
					}
					reportInfos={
						isAuthor
							? undefined
							: {
									contentId: quiz.id,
									contentType: "QUIZ",
								}
					}
				>
					<MenuRow
						onClick={() =>
							createModal((close) => (
								<EditReferencePostModal close={close} shared={{ type: "SHARE_QUIZ", reference: quiz }} />
							))
						}
					>
						<IconWrapper>
							<RepostIcon />
						</IconWrapper>
						<span>{I18n.t("share")}</span>
					</MenuRow>
				</SimpleMenuContent>
			</StyledMenu>
			<Bottom small={smallVersion}>
				<AuthLink to={Path.Quiz(id, isMobile)}>
					<StartButton lighter={smallVersion}>
						{I18n.t(quiz.participants.byMe ? "quiz.thumbnail.review" : "quiz.thumbnail.start")}
					</StartButton>
				</AuthLink>
				{displayStatus && <StyledCourseStatusLabel status={status} />}
			</Bottom>
		</Container>
	);
};

const Container = styled.div.withConfig({
	shouldForwardProp: (prop) => !["small", "color"].includes(prop),
})<{ small?: boolean; color?: string }>`
	background-color: ${({ theme, color }) => color ?? theme.colors.vividBlue};
	${({ theme }) => theme.colors.white};
	border-radius: 12px;
	overflow: hidden;
	> * {
		position: relative;
	}
	position: relative;
	padding: 65px 24px 24px;
	${({ small }) =>
		small &&
		css`
			padding: 15px;
		`}

	${stack(10, "space-between")};
	// :hover{
	// 	background:${({ theme, color }) => color ?? theme.colors.vividBlue};
	// 	background-color:rgba(0,0,0,0.5);
	// }
	transition: background 0.5s ease-out;
`;

const CategoryImage = styled.img.withConfig({
	shouldForwardProp: (prop) => prop !== "small",
})<{ small?: boolean }>`
	position: absolute;
	height: 240px;
	max-width: 100%;
	right: -60px;
	bottom: -80px;
	opacity: 0.3;
	${({ small }) =>
		small &&
		css`
			right: -14px;
			bottom: -22px;
			width: 60px;
			height: 102px;
		`}
	filter: invert(100%) sepia(11%) saturate(18%) hue-rotate(347deg) brightness(200%) contrast(105%);
`;

const WhitePrimary = styled(Primary)<{ small?: boolean }>`
	color: ${({ theme }) => theme.colors.white};

	${({ small }) =>
		!small &&
		css`
			font-weight: 700;
		`}
`;

const StyledCategoryLabel = styled(CategoryLabel).withConfig({
	shouldForwardProp: (prop) => prop !== "small",
})<{ small?: boolean }>`
	flex-shrink: 0;
	max-width: 180px;
	${({ small }) =>
		!small &&
		css`
			position: absolute;
			right: 65px;
			top: 24px;
		`}
`;

const StyledBadgeLabel = styled(BadgeLabel)`
	position: absolute;
	position: absolute;
	left: 20px;
	top: 0px;
`;

const Description = styled(DraftRenderer).withConfig({
	shouldForwardProp: (prop) => prop !== "small",
})<{ small?: boolean }>`
	${({ theme, small }) => (small ? theme.textStyles.light : theme.textStyles.primary)};
	color: ${({ theme }) => theme.colors.white};
	${({ small }) => small && "line-height: 1rem"};
	max-width: 325px;
`;

const Title = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "small",
})<{ small?: boolean }>`
	${({ theme, small }) => (small ? theme.textStyles.primaryBold : theme.textStyles.title)};
	color: ${({ theme }) => theme.colors.white};
	${({ small }) => !small && "max-width: 360px"};
	word-break: break-word;
`;

const InfoIconWrapper = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "small",
})<{ small?: boolean }>`
	${({ small }) =>
		!small &&
		css`
			display: flex;
			align-items: center;
			flex-shrink: 0;
			font-weight: 700 !important;
			gap: 5px;
		`}
	// width: 24px;
	color: ${({ theme }) => theme.colors.white};
	svg {
		width: 23px;
		height: 16px;
		path {
			fill: ${({ theme }) => theme.colors.white};
		}
	}
`;

const Bottom = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "small",
})<{ small?: boolean }>`
	margin-top: 16px;
	${({ small }) => small && `${stack(10, "flex-start", "center")}`}
	${row(10, "space-between")};
	align-self: stretch;
	flex-wrap: wrap;
`;

const StartButton = styled(PrimaryButton)`
	padding: 12px 20px;
`;

const StyledCourseStatusLabel = styled(CourseStatusLabel)`
	margin-top: 5px;
`;

const StyledMenu = styled(KebabMenu).withConfig({
	shouldForwardProp: (prop) => prop !== "small",
})<{ small?: boolean }>`
	position: absolute;
	right: 24px;
	top: 24px;

	${({ small }) =>
		small &&
		css`
			right: 15px;
			top: 15px;
		`}
`;

const StyledUserView = styled(UserView)`
	margin-top: 10px;
	color: ${({ theme }) => theme.colors.white};
`;
